import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, ArrowLeft, Phone, Mail } from 'lucide-react';
import { Section, Button } from '../components/ui/Common.jsx';
import ResponsiveImage from '../components/ui/ResponsiveImage.jsx';
import { services, companyInfo } from '../data.js';

export default function ServicePage() {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{service.title} | Sydthy Taxi & Turistfart ApS</title>
        <meta name="description" content={service.description} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImage
            src={service.image}
            alt={service.title}
            className="h-full w-full object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-slate-900/60" />
        </div>
        <div className="container relative z-10 mx-auto flex h-full items-end pb-20 px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-blue-400 hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
            </Link>
            <h1 className="text-5xl font-bold text-white md:text-6xl">{service.title}</h1>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <Section>
        <div className="grid gap-16 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <h2 className="text-3xl font-bold">Professionel løsning til dine behov</h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              {service.longDescription}
            </p>
            <div className="grid gap-6 sm:grid-cols-2">
              {service.features.map((feature, idx) => (
                <div key={idx} className="flex gap-3">
                  <CheckCircle2 className="h-6 w-6 shrink-0 text-blue-600" />
                  <span className="text-lg font-medium text-slate-700">{feature}</span>
                </div>
              ))}
            </div>
            <div className="pt-8">
              <ResponsiveImage
                src={service.image}
                alt={service.title}
                className="w-full h-96 object-cover"
                sizes="(max-width: 1024px) 100vw, 800px"
              />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-8">
              <div className="bg-slate-900 p-8 text-white">
                <h3 className="mb-6 text-2xl font-bold">Bestil transport</h3>
                <p className="mb-8 text-slate-400">
                  Kontakt os for et uforpligtende tilbud eller for at booke din næste tur med det samme.
                </p>
                <div className="space-y-4">
                  <a
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    className="flex w-full items-center justify-center gap-3 bg-blue-600 py-4 font-bold transition-colors hover:bg-blue-700"
                  >
                    <Phone className="h-5 w-5" />
                    Ring {companyInfo.phone}
                  </a>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex w-full items-center justify-center gap-3 border border-white/20 py-4 font-bold transition-colors hover:bg-white/10"
                  >
                    <Mail className="h-5 w-5" />
                    Send en mail
                  </a>
                </div>
              </div>

              <div className="bg-slate-50 p-8 border border-slate-200">
                <h4 className="mb-4 font-bold">Andre ydelser</h4>
                <ul className="space-y-3">
                  {services
                    .filter((s) => s.id !== id)
                    .map((s) => (
                      <li key={s.id}>
                        <Link
                          to={s.id === 'taxa' ? '/taxa' : `/ydelser/${s.id}`}
                          className="flex items-center justify-between text-slate-600 hover:text-blue-600 transition-colors"
                        >
                          {s.title}
                          <ArrowLeft className="h-4 w-4 rotate-180" />
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
